import React, { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

function Dashboard() {
    const [progress, setProgress] = useState([]);
    const [overall, setOverall] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [chartError, setChartError] = useState('');
    const chartRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchProgress = async () => {
            try {
                setIsLoading(true);
                setError('');
                setChartError('');
                const response = await axios.get(`${API_URL}/progress`, { withCredentials: true });
                console.log('Raw API response:', response);
                console.log('Progress data:', response.data);
                console.log('Books:', response.data.books);
                console.log('Overall percentage:', response.data.overall_percentage);
                const books = Array.isArray(response.data.books) ? response.data.books : [];
                setProgress(books);
                setOverall(response.data.overall_percentage || 0);
                if (!books.length) {
                    console.log('No books data returned from API');
                } else {
                    console.log('Books data structure:', books);
                }
            } catch (err) {
                console.error('Error fetching progress:', err);
                const errorMessage = err.response?.data?.error || 'Failed to load progress data. Please try again later.';
                setError(errorMessage);
                console.log('Error set:', errorMessage);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProgress();
    }, []);

    useEffect(() => {
        if (!progress.length) {
            console.log('No progress data to render chart');
            return;
        }

        const ctx = document.getElementById('progressChart')?.getContext('2d');
        if (!ctx) {
            console.log('Canvas context not found - check if canvas element exists in DOM');
            setChartError('Failed to render chart: Canvas element not found.');
            return;
        }

        if (chartRef.current) {
            chartRef.current.destroy();
            console.log('Previous chart instance destroyed');
        }

        // Create the gradient once, outside the map function
        let gradient;
        try {
            gradient = ctx.createLinearGradient(0, 0, 400, 0);
            gradient.addColorStop(0, 'rgba(52, 152, 219, 0.8)');
            gradient.addColorStop(1, 'rgba(41, 128, 185, 0.8)');
        } catch (err) {
            console.error('Error creating gradient:', err);
            setChartError('Failed to create chart gradient: ' + err.message);
            return;
        }

        const chartData = {
            labels: progress.map(book => book.book_name),
            datasets: [{
                label: 'Progress (%)',
                data: progress.map(book => {
                    const percentage = (book.chapters_read.length / book.total_chapters * 100).toFixed(2);
                    console.log(`Calculating percentage for ${book.book_name}: ${book.chapters_read.length} / ${book.total_chapters} = ${percentage}%`);
                    return percentage;
                }),
                backgroundColor: progress.map(book => {
                    const percentage = book.chapters_read.length / book.total_chapters * 100;
                    if (percentage === 100) return 'rgba(34, 197, 94, 0.8)';
                    return gradient;
                }),
                borderColor: progress.map(book => {
                    const percentage = book.chapters_read.length / book.total_chapters * 100;
                    if (percentage === 100) return 'rgba(34, 197, 94, 1)';
                    return 'rgba(41, 128, 185, 1)';
                }),
                borderWidth: 1
            }]
        };
        console.log('Chart data prepared:', chartData);

        try {
            chartRef.current = new Chart(ctx, {
                type: 'bar',
                data: chartData,
                options: {
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                        x: {
                            beginAtZero: true,
                            max: 100,
                            ticks: { stepSize: 10 },
                            grid: { color: 'rgba(0, 0, 0, 0.1)' }
                        },
                        y: {
                            ticks: { autoSkip: false, maxRotation: 0, minRotation: 0 },
                            grid: { display: false }
                        }
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: 12,
                            callbacks: {
                                label: function(context) {
                                    const book = progress[context.dataIndex];
                                    const chaptersRead = book.chapters_read.length;
                                    const totalChapters = book.total_chapters;
                                    const percentage = (chaptersRead / totalChapters * 100).toFixed(2);
                                    return `${book.book_name}: ${percentage}% (${chaptersRead}/${totalChapters} chapters)`;
                                }
                            }
                        }
                    }
                }
            });
            console.log('Chart instance created:', chartRef.current);
        } catch (err) {
            console.error('Error creating chart:', err);
            setChartError('Failed to render chart: ' + err.message);
        }

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
                console.log('Chart instance destroyed');
            }
        };
    }, [progress]);

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="md:flex md:items-center md:justify-between mb-8">
                    <div className="flex-1 min-w-0">
                        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                            Reading Progress Dashboard
                        </h2>
                    </div>
                    <div className="mt-4 flex md:mt-0 md:ml-4">
                        <button
                            onClick={() => navigate('/logprogress')}
                            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Log New Progress
                        </button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : error ? (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                ) : progress.length === 0 ? (
                    <div className="rounded-md bg-yellow-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-yellow-700">
                                    No progress data available. This might be due to a session issue or no recorded progress for your user account. Please try logging progress or contact support.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : chartError ? (
                    <div className="rounded-md bg-red-50 p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm text-red-700">{chartError}</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="mb-6">
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-medium text-gray-900">Overall Progress</h3>
                                <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                                    {overall}% Complete
                                </span>
                            </div>
                        </div>
                        <div className="h-[500px]">
                            <canvas id="progressChart"></canvas>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Dashboard;